import { hc } from "hono/client";
import { noteVersion } from "@optymyse/suite-ui";
import type { AppType } from "../../shared/types/api";

/**
 * Asks Steer's own API, on this page's host with its session cookie, and hands the answer to the kit's noteVersion
 * before anything reads it, a refusal or a failure as much as a success (SuiteUi's README, "A new version"). The API
 * names the version of steer-app that answered (`x-app-version`, src/worker/http.ts), and one this page hasn't heard
 * before is a deploy since it opened, which the kit's notice says (src/client/App.tsx). Every request the page makes to
 * the API and /auth goes through here: the typed client below, signing out, a file sent to an issue or to an import, and
 * People's lists and changes. The live socket's upgrade names no version, and what anyone else serves, an
 * organisation's logo from Impa's media, the browser fetches itself: this never sees either.
 */
export async function askApi(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, { ...init, credentials: "same-origin" });
  noteVersion(response);
  return response;
}

// Headers as they come, whatever their form (an object, a Headers or pairs), with the JSON type set.
const customFetch: typeof fetch = (input, init) => {
  const headers = new Headers(init?.headers);
  headers.set("Content-Type", "application/json");
  return askApi(input, { ...init, headers });
};

export const api = hc<AppType>("/", { fetch: customFetch });

export type ApiErrorBody = {
  error: {
    code: string;
    message: string;
    details?: unknown;
    request_id?: string;
  };
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: ApiErrorBody,
  ) {
    super(body.error.message);
  }
}

/** An answer's refusal, in the suite's envelope, as an ApiError, whatever the answer's body holds. */
async function refusalOf(res: Response): Promise<ApiError> {
  const body = (await res.json().catch(() => ({}))) as Partial<ApiErrorBody>;
  return new ApiError(res.status, {
    error: {
      code: body.error?.code ?? "unknown",
      message: body.error?.message ?? "Request failed",
      details: body.error?.details,
      ...(body.error?.request_id === undefined ? {} : { request_id: body.error.request_id }),
    },
  });
}

/**
 * Whether a change is sent again (BUILD_SPEC §4.6): one that never got an answer — the network down, or back a moment
 * before it carries anything, as it can be just after the device comes online — up to twice more, a second and then
 * two apart; never one Steer answered, whatever it said. What a person makes carries one idempotency key however
 * often it is sent (`idempotencyKeyFor`), so it is made once.
 */
export const retryUnanswered = (failures: number, error: unknown) => failures < 3 && !(error instanceof ApiError);
export const retryDelay = (attempt: number) => Math.min(1_000 * 2 ** attempt, 8_000);

const keys = new WeakMap<object, string>();

/** One key for a change however often it is sent, the change being the same object each time (TanStack's). */
export function idempotencyKeyFor(change: object): string {
  let key = keys.get(change);
  if (key === undefined) {
    key = `console:${crypto.randomUUID()}`;
    keys.set(change, key);
  }
  return key;
}

/** An answer's JSON, or its refusal thrown. */
export async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) throw await refusalOf(res);
  return (await res.json()) as T;
}

/** An answer with nothing in it, or its refusal thrown. */
export async function expectNoContent(res: Response): Promise<void> {
  if (!res.ok) throw await refusalOf(res);
}

export async function ensureOk<T>(promise: Promise<Response>): Promise<T> {
  const res = await promise;
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorBody;
    throw new ApiError(res.status, body);
  }
  return (await res.json()) as T;
}
